import { useState, useEffect, useCallback } from 'react'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { relativeTimestamp } from '@opallabs/lambda'

/**
 * Wrapper for `@opallabs/lambda`’s `relativeTimestamp` that updates at an
 * undocumented slow interval. It ensures changes in `relativeTimestamp`’s
 * output are reflected while minimizing performance impact.
 */
export function useRelativeTimestamp(
  timestamp: Date | string,
  locale: IETFLanguageTag
) {
  const calculateDisplayedTimestamp = useCallback(
    () => relativeTimestamp(new Date(timestamp), { locale }),
    [locale, timestamp]
  )

  const [displayedTimestamp, setDisplayedTimestamp] = useState(
    calculateDisplayedTimestamp()
  )

  useEffect(() => {
    // @todo Optional optimization: don't call `setInterval` if `timestamp` is
    // a different year, because the output will never change. (This is an
    // assumption about the implementation of `relativeTimestamp`, but it seems
    // like a safe one.) Skipping `setInterval`, or changing the interval
    // duration, aren't safe for any other case because the output *can* change,
    // and it would be confusing if timestamps visible in the same view didn't
    // update at the same time--for example, the clock rolling over to midnight,
    // which changes the output from time to day name.
    const intervalId = setInterval(() => {
      setDisplayedTimestamp(calculateDisplayedTimestamp())
    }, 1000 * 60)

    return () => clearInterval(intervalId)
  }, [calculateDisplayedTimestamp])

  return displayedTimestamp
}
